/* eslint-disable indent */
import { GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { ParsedUrlQuery } from "querystring";
import { SeoHead } from "../../../containers/components";
import { Page } from "../../../containers/pages/page";
import {
  getAllSectionFromPageCache,
  getFooterCache,
  getNavBarCache,
} from "../../../services/cache";
import {
  generatePageVariables,
  getPageSeo,
  PageVariables,
} from "../../../services/pageVariables";
import { getSearchMaps } from "../../../services/searchMap";
import { StrapiApi } from "../../../services/strapiApi";
import { TransportationType } from "../../../services/transportationTypes";
import {
  CityType,
  FooterSectionType,
  GetStaticPath,
  NavigationBarType,
  PageBaseProps,
  PageBaseType,
  PageSectionType,
  PageSeo,
  PreFooters,
  SectionsType,
  TransportationTypeEnum,
} from "../../../types";
import { PlacePageParams } from "./[place]";
import { stores } from "../../../services/store";
import { getCityPage } from "../../vtc/[city]";

export interface CityPageParams extends ParsedUrlQuery {
  type: string;
  city: string;
}

export interface CityProps {
  pageSections: PageSectionType<SectionsType>[];
  navBarSection: PageSectionType<NavigationBarType>;
  footerSection: PageSectionType<FooterSectionType>;
  preFooters?: PreFooters;
  hasBooker: boolean;
  reassurances: string[];
  seo: PageSeo;
  variables?: PageVariables;
}

export default function City(props: CityProps): JSX.Element {
  return (
    <>
      <SeoHead title={props.seo?.title} description={props.seo?.description} />
      <Page
        pageSections={props.pageSections}
        navBarSection={props.navBarSection}
        footerSection={props.footerSection}
        preFooters={props.preFooters}
        hasBooker={props.hasBooker}
        variables={props.variables}
        reassurances={props.reassurances}
      />
    </>
  );
}

export async function getStaticProps(
  context: GetStaticPropsContext
): Promise<PageBaseProps> {
  const { params, locale } = context;
  const { city } = params as CityPageParams;
  const type = "taxi";
  const currentCity = await stores.cities.getEntryFromStore(city);
  if (!currentCity) {
    return { notFound: true };
  }

  const page = await getCityPage(
    type as TransportationTypeEnum,
    currentCity,
    locale ?? "fr"
  );

  const navBarSection = await getNavBarCache();
  const footer = await getFooterCache();
  const variables = generatePageVariables(
    Object.assign({ type }, params) as PlacePageParams,
    undefined,
    currentCity
  );
  const preFooters = getSearchMaps(city, type as TransportationType);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? "fr")),
      pageSections: page
        ? await getAllSectionFromPageCache(page, `${type}-`)
        : [],
      navBarSection: navBarSection,
      footerSection: footer,
      seo: getPageSeo(page?.seo, variables, "city"),
      hasBooker: page ? page.booker : true,
      reassurances: page?.reassurances?.map((elem) => elem.value) ?? null,
      page,
      variables,
      preFooters,
    },
    revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME),
  };
}

export async function getStaticPaths(): Promise<GetStaticPath> {
  //return { paths: [], fallback: false };
  const taxiPages = await stores.taxi.listEntriesFromStore();

  const paths = [
    ...taxiPages
      .filter((page) => page.slug !== "default")
      .map((page) => `/taxi/${page.slug}`),
  ];

  return {
    paths,
    fallback: false,
  };
}
